import Button from 'components/form/Button';
import SeachInput from 'components/form/SeachInput';
import Card from 'components/Layout/Card';
import Table from 'components/Layout/Table';
import {
  fetchParticipantRequestData,
  handleSendParticipantOtp,
  handleVerifyParticipantOtpRequest,
} from 'store/slices';
import {
  getDataFromLocalStorage,
  getUserType,
  objectToFormData,
  paymentColor,
} from 'utils/helpers';
import { limit, tabs } from 'utils/constants';
import { useEffect, useState } from 'react';
import { useDispatch} from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import './ParticipantsRequest.scss';
import usePermission from 'hooks/usePermission';
import TabLayout from 'components/Layout/TabLayout';
import BulkAction from '../../Common/BulkAction';

const ParticipantsRequest = () => {
  // Hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const { eventId } = params;
  
  // Permissions
  const memberType = getUserType();
  const userType = getDataFromLocalStorage('user_type');
  const { viewAccess } = usePermission("Requests");

  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [tableList, setTableList] = useState([]);
  const [childTabType, setChildTabType] = useState('0');
  const [searchText, setSearchText] = useState('');
  const [timer, setTimer] = useState('');
  const [eventName, setEventName] = useState('');
  const [searchPayload, setSearchPayload] = useState({
    event_name: '',
    event_type: '',
  });
  const [filterData, setFilterData] = useState({
    total: 0,
    offset: 0,
    limit: limit,
    event_by_filter: '',
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [isCheckAll, setIsCheckAll] = useState(false);

    // URL type for navigation
  const urlType = ['0', '1'].includes(userType)
    ? 'request-received'
    : 'raised-request';

    
  // Table header
  const header = [
    ...(childTabType === '0' && userType !== '6' ? [{ isSearch: false }] : []),
    { isSearch: false, searchInputName: '', title: 'Profile ID' },
    { isSearch: false, searchInputName: '', title: 'Name' },
    { isSearch: false, searchInputName: '', title: 'Email Id' },
    { isSearch: false, searchInputName: '', title: 'Mobile' },
    { isSearch: false, searchInputName: '', title: 'Type Of Participation' },
    { isSearch: false, searchInputName: '', title: 'Status' },
    { isSearch: false, title: 'Action' },
  ];

  // Fetch requests data
  const getRequestsData = async (obj) => {
    setIsLoading(true);
    const updatedObj = { ...obj };
    if (['0', '1', '6'].includes(userType)) {
      updatedObj.tab_status = childTabType;
    }
    const finalObj = { ...updatedObj, event_id: eventId };
    let forData = objectToFormData(finalObj);
    const response = await dispatch(fetchParticipantRequestData(forData));
    setTableList(response?.data?.events_registered_members || []);
    setFilterData({
      ...obj,
      total: response?.data?.result_count || 0,
    });
    setEventName(response?.data?.event_name);
    setIsLoading(false);
  };

  // Search, filter, and pagination handlers
  const handelChangeSearch = (searchData) => {
    setIsLoading(true);
    let newData = { ...filterData, ...searchData, offset: 0 };
    setSearchPayload(searchData);
    setFilterData(newData);
    getRequestsData(newData);
  };

  const handelChangePagination = (offset, newLimit) => {
    setIsLoading(true);
    const updatedData = {
      ...filterData,
      ...searchPayload,
      offset,
      limit: newLimit ?? filterData.limit,
    };
    setFilterData(updatedData);
    getRequestsData(updatedData);
  };

  const handelChangeFilter = (val) => {
    setIsLoading(true);
    let newData = {
      ...filterData,
      ...searchPayload,
      event_by_filter: val,
      offset: 0,
    };
    setFilterData(newData);
    getRequestsData(newData);
  };

  // Effect: fetch data on tab change
  useEffect(() => {
    const payload = { ...filterData, ...searchPayload };
    getRequestsData(payload);
    // eslint-disable-next-line
  }, [childTabType]);

  // Debounced search handler
  const handleSearch = (e) => {
    let value = e?.target?.value;
    setSearchText(value);
    let time = timer;
    clearTimeout(time);
    time = setTimeout(() => {
      const updatedSearchPayload = { ...searchPayload, global_filter: value };
      setSearchPayload(updatedSearchPayload);
      const updatedFilterData = {
        ...filterData,
        offset: 0,
        ...updatedSearchPayload,
      };
      setIsLoading(true);
      setFilterData(updatedFilterData);
      getRequestsData(updatedFilterData);
    }, 800);
    setTimer(time);
  };

  // Table row data
  const rowData = [];
  tableList?.forEach((elem) => {
    let obj = [
      ...(childTabType === '0' && userType !== '6'
        ? [
          {
            value: (
              <div className="d-flex justify-content-center align-items-center">
                <input
                  type="checkbox"
                  className={`custom-checkbox ${selectedIds.includes(elem?.request_id)
                    ? 'activeChecked'
                    : 'activeUnchecked'
                    }`}
                  onChange={(e) =>
                    handleCheckboxChange(elem?.request_id, e.target.checked)
                  }
                  checked={selectedIds.includes(elem?.request_id)}
                />
              </div>
            ),
          },
        ]
        : []),
      { value: elem.profile_id },
      { value: elem.name },
      { value: elem.email_id },
      { value: elem.phone_number },
      { value: elem.join_as },
      {
        value: <div className={paymentColor(elem?.status)}>{elem?.status}</div>,
      },
      {
        value: (
          <span className="action-icon-buttons">
            {viewAccess === '1' && (
              <Button
                btnStyle="light-outline"
                icon={<i className="bi bi-eye" />}
                className="me-2"
                onClick={() => {
                  navigate(
                    `/${memberType}/${urlType}/participants-event/request/${eventId}/view/${elem?.request_id}`
                  );
                }}
                isSquare
              />
            )}
          </span>
        ),
      },
    ];
    rowData.push({ data: obj });
  });

  // Checkbox handlers
  const handleSelectAllChange = () => {
    setIsCheckAll((prev) => !prev);
    setSelectedIds(
      isCheckAll ? [] : tableList.map(({ request_id }) => request_id)
    );
  };

  const handleCheckboxChange = (request_id, isChecked) => {
    setSelectedIds((prev) => {
      const updatedSelectedIds = isChecked
        ? [...prev, request_id]
        : prev.filter((selectedId) => selectedId !== request_id);
      setIsCheckAll(updatedSelectedIds.length === tableList.length);
      return updatedSelectedIds;
    });
  };

  return (
    <div id="participants-request-container">
      {/* Main Card */}
      <Card className="cps-20 cpe-20 cpb-20">
        {["0", "1"].includes(userType) && (
          <div className="event-management-tab cpt-14">
            <TabLayout
              tabs={tabs}
              activeKey={childTabType}
              onChange={setChildTabType}
            />
          </div>
        )}

        {/* Table Controls */}
        <div className="d-flex justify-content-between align-items-center cpt-15 cpb-15">
          {selectedIds.length === 0 ? (
            <div className="table-title">{eventName}</div>
          ) : (
            <BulkAction
              isCheckAll={isCheckAll}
              handleSelectAllChange={handleSelectAllChange}
              isLoading={isLoading}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              handleSendOtp={handleSendParticipantOtp}
              handleVerifyOtpRequest={handleVerifyParticipantOtpRequest}
              typeTitle="Participants"
              onSuccess={() => getRequestsData({ ...filterData, ...searchPayload })}
            />
          )}
          <div style={{ width: '150px' }} className="me-3">
            <SeachInput
              placeholder="Search"
              value={searchText}
              onChange={handleSearch}
              className="small-text-input"
              iconClassName="small-search-icon"
            />
          </div>
        </div>

        {/* Table */}
        <Table
          isLoading={isLoading}
          header={header}
          rowData={rowData}
          filterData={filterData}
          searchPayload={searchPayload}
          searchInputChange={handelChangeSearch}
          changeOffset={handelChangePagination}
          maxHeight="60vh"
          showRowsLimit={['0', '1'].includes(userType)}
        />
      </Card>
    </div>
  );
};

export default ParticipantsRequest;



// import Button from 'components/form/Button';
// import SeachInput from 'components/form/SeachInput';
// import Card from 'components/Layout/Card';
// import Table from 'components/Layout/Table';
// import CheckBox from 'components/form/CheckBox';
// import ActionFormPopup from '../../Common/ActionFormPopup';
// import RequestVerificationModal from '../../Common/RequestVerificationModal';
// import VerifyOtpModel from '../../Common/VerifyOtpModel';
// import {
//   fetchParticipantRequestData,
//   handleSendParticipantOtp,
//   handleVerifyParticipantOtpRequest,
//   showSuccess,
//   throwError,
// } from 'store/slices';
// import {
//   getDataFromLocalStorage,
//   getUserType,
//   objectToFormData,
//   paymentColor,
// } from 'utils/helpers';
// import { limit } from 'utils/constants';
// import { useEffect, useState } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { useNavigate, useParams } from 'react-router-dom';
// import './ParticipantsRequest.scss';
// import usePermission from 'hooks/usePermission';

// const ParticipantsRequest = () => {
//   // Hooks
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const params = useParams();
//   const { eventId } = params;
  
//   // Permissions
//   const memberType = getUserType();
//   const userType = getDataFromLocalStorage('user_type');
//   const { viewAccess, createAccess } = usePermission("Requests");

//   // Local state
//   const [isLoading, setIsLoading] = useState(false);
//   const [tableList, setTableList] = useState([]);
//   const [childTabType, setChildTabType] = useState('1');
//   const [searchText, setSearchText] = useState('');
//   const [timer, setTimer] = useState('');
//   const [openActionForm, setOpenActionForm] = useState(false);
//   const [eventName, setEventName] = useState('');
//   const [searchPayload, setSearchPayload] = useState({
//     event_name: '',
//     event_type: '',
//   });
//   const [filterData, setFilterData] = useState({
//     total: 0,
//     offset: 0,
//     limit: limit,
//     event_by_filter: '',
//   });
//   const [selectedIds, setSelectedIds] = useState([]);
//   const [isCheckAll, setIsCheckAll] = useState(false);
//   const [isRejecting, setIsRejecting] = useState(false);
//   const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] =
//     useState(false);
//   const [otpData, setOtpData] = useState({});
//   const [showOtpDialog, setShowOtpDialog] = useState(false);
//   const [otpVerification, setOtpVerification] = useState(false);
//   const [btnLoading, setBtnLoading] = useState(false);

//   // Table header
//   const header = [
//     ...(childTabType === '1' && userType !== '6' ? [{ isSearch: false }] : []),
//     { isSearch: false, searchInputName: '', title: 'Profile ID' },
//     { isSearch: false, searchInputName: '', title: 'Name' },
//     { isSearch: false, searchInputName: '', title: 'Email Id' },
//     { isSearch: false, searchInputName: '', title: 'Mobile' },
//     { isSearch: false, searchInputName: '', title: 'Type Of Participation' },
//     { isSearch: false, searchInputName: '', title: 'Status' },
//     { isSearch: false, title: 'Action' },
//   ];

//   // Fetch requests data
//   const getRequestsData = async (obj) => {
//     setIsLoading(true);
//     const updatedObj = { ...obj };
//     if (['0', '1', '6'].includes(userType)) {
//       updatedObj.tab_status = childTabType === '1' ? '0' : '1';
//     }
//     const finalObj = { ...updatedObj, event_id: eventId };
//     let forData = objectToFormData(finalObj);
//     const response = await dispatch(fetchParticipantRequestData(forData));
//     setTableList(response?.data?.events_registered_members || []);
//     setFilterData({
//       ...obj,
//       total: response?.data?.result_count || 0,
//     });
//     setEventName(response?.data?.event_name);
//     setIsLoading(false);
//   };

//   // Search, filter, and pagination handlers
//   const handelChangeSearch = (searchData) => {
//     setIsLoading(true);
//     let newData = { ...filterData, ...searchData, offset: 0 };
//     setSearchPayload(searchData);
//     setFilterData(newData);
//     getRequestsData(newData);
//   };

//   const handelChangePagination = (offset, newLimit) => {
//     setIsLoading(true);
//     const updatedData = {
//       ...filterData,
//       ...searchPayload,
//       offset,
//       limit: newLimit ?? filterData.limit,
//     };
//     setFilterData(updatedData);
//     getRequestsData(updatedData);
//   };

//   const handelChangeFilter = (val) => {
//     setIsLoading(true);
//     let newData = {
//       ...filterData,
//       ...searchPayload,
//       event_by_filter: val,
//       offset: 0,
//     };
//     setFilterData(newData);
//     getRequestsData(newData);
//   };

//   // Effect: fetch data on tab change
//   useEffect(() => {
//     const payload = { ...filterData, ...searchPayload };
//     getRequestsData(payload);
//     // eslint-disable-next-line
//   }, [childTabType]);

//   // Debounced search handler
//   const handleSearch = (e) => {
//     let value = e?.target?.value;
//     setSearchText(value);
//     let time = timer;
//     clearTimeout(time);
//     time = setTimeout(() => {
//       const updatedSearchPayload = { ...searchPayload, global_filter: value };
//       setSearchPayload(updatedSearchPayload);
//       const updatedFilterData = {
//         ...filterData,
//         offset: 0,
//         ...updatedSearchPayload,
//       };
//       setIsLoading(true);
//       setFilterData(updatedFilterData);
//       getRequestsData(updatedFilterData);
//     }, 800);
//     setTimer(time);
//   };

//   // Table row data
//   const rowData = [];
//   tableList?.forEach((elem) => {
//     let obj = [
//       ...(childTabType === '1' && userType !== '6'
//         ? [
//           {
//             value: (
//               <div className="d-flex justify-content-center align-items-center">
//                 <input
//                   type="checkbox"
//                   className={`custom-checkbox ${selectedIds.includes(elem?.request_id)
//                     ? 'activeChecked'
//                     : 'activeUnchecked'
//                     }`}
//                   onChange={(e) =>
//                     handleCheckboxChange(elem?.request_id, e.target.checked)
//                   }
//                   checked={selectedIds.includes(elem?.request_id)}
//                 />
//               </div>
//             ),
//           },
//         ]
//         : []),
//       { value: elem.profile_id },
//       { value: elem.name },
//       { value: elem.email_id },
//       { value: elem.phone_number },
//       { value: elem.join_as },
//       {
//         value: <div className={paymentColor(elem?.status)}>{elem?.status}</div>,
//       },
//       {
//         value: (
//           <span className="action-icon-buttons">
//             {viewAccess === '1' && (
//               <Button
//                 btnStyle="light-outline"
//                 icon={<i className="bi bi-eye" />}
//                 className="me-2"
//                 onClick={() => {
//                   navigate(
//                     `/${memberType}/${urlType}/participants-event/request/${eventId}/view/${elem?.request_id}`
//                   );
//                 }}
//                 isSquare
//               />
//             )}
//           </span>
//         ),
//       },
//     ];
//     rowData.push({ data: obj });
//   });

//   // Checkbox handlers
//   const handleSelectAllChange = () => {
//     setIsCheckAll((prev) => !prev);
//     setSelectedIds(
//       isCheckAll ? [] : tableList.map(({ request_id }) => request_id)
//     );
//   };

//   const handleCheckboxChange = (request_id, isChecked) => {
//     setSelectedIds((prev) => {
//       const updatedSelectedIds = isChecked
//         ? [...prev, request_id]
//         : prev.filter((selectedId) => selectedId !== request_id);
//       setIsCheckAll(updatedSelectedIds.length === tableList.length);
//       return updatedSelectedIds;
//     });
//   };

//   // Action form handlers
//   const initialValues = { comment: '', document: '' };

//   const handleReject = async (values, setErrors) => {
//     setIsRejecting(true);
//     if (!values.comment.trim() && !values.document) {
//       setErrors({
//         comment: 'Either a comment or a document is required',
//         document: 'Either a comment or a document is required',
//       });
//       return;
//     }
//     setOtpData({
//       comment: values?.comment,
//       document: values?.document,
//       action: '2', // Reject
//     });
//     await sendOtpRequest();
//   };

//   const handleApproveRequest = async (values, setErrors) => {
//     setOtpData({
//       comment: values?.comment,
//       document: values?.document,
//       action: '1', // Approve
//     });
//     if (userType === '1') {
//       await sendOtpRequest();
//     } else {
//       setIsOpenConfirmationDialog(true);
//       setOpenActionForm(false);
//     }
//   };

//   const sendOtpRequest = async () => {
//     setBtnLoading(true);
//     const payload = objectToFormData({
//       request_ids: selectedIds,
//     });
//     const response = await dispatch(handleSendParticipantOtp(payload));
//     if (response.status === 200) {
//       setShowOtpDialog(true);
//       setOpenActionForm(false);
//       dispatch(showSuccess(response?.message));
//       setBtnLoading(false);
//     } else {
//       setBtnLoading(false);
//       dispatch(throwError(response));
//     }
//   };

//   const handleVerifyOtp = async (otp) => {
//     setOtpVerification(true);
//     const payload = objectToFormData({
//       request_ids: selectedIds,
//       ...(otp && { otp }),
//       document: otpData?.document,
//       comment: otpData?.comment,
//       request_status: otpData?.action,
//       request_data: 1,
//     });
//     const response = await dispatch(handleVerifyParticipantOtpRequest(payload));
//     if (response.status === 200) {
//       setShowOtpDialog(false);
//       dispatch(showSuccess(response.message));
//       setIsOpenConfirmationDialog(false);
//       setOtpVerification(false);
//       getRequestsData({ ...filterData, ...searchPayload });
//       setSelectedIds([]);
//     } else {
//       dispatch(throwError(response));
//       setOtpVerification(false);
//     }
//   };

//   // URL type for navigation
//   const urlType = ['0', '1'].includes(userType)
//     ? 'request-received'
//     : 'raised-request';

//   // Render
//   return (
//     <div id="participants-request-container">
//       {/* Action Form Popup */}
//       {openActionForm && (
//         <ActionFormPopup
//           showApproveDialog={openActionForm}
//           onHide={() => setOpenActionForm(false)}
//           setIsRejecting={setIsRejecting}
//           isRejecting={isRejecting}
//           handleApproveRequest={handleApproveRequest}
//           handleReject={handleReject}
//           initialValues={initialValues}
//           btnLoading={btnLoading}
//         />
//       )}

//       {/* Confirmation Modal */}
//       {isOpenConfirmationDialog && (
//         <RequestVerificationModal
//           handleConfirmation={() => handleVerifyOtp()}
//           onHide={() => setIsOpenConfirmationDialog(false)}
//           title="You want to share the approve request to super admin"
//           isLoading={otpVerification}
//         />
//       )}

//       {/* OTP Modal */}
//       {showOtpDialog && (
//         <VerifyOtpModel
//           title="Participants Request"
//           showApproveDialog={showOtpDialog}
//           onHide={() => setShowOtpDialog(false)}
//           onVerify={handleVerifyOtp}
//           sendOtpRequest={sendOtpRequest}
//           otpVerification={otpVerification}
//         />
//       )}

//       {/* Main Card */}
//       <Card className="cps-20 cpe-20 cpb-20">
//         {/* Tabs */}
//         <div className="participants-request-tab cpt-14">
//           <div
//             className={`tab ${childTabType === '1' ? 'active' : ''}`}
//             onClick={() => setChildTabType('1')}
//           >
//             Pending
//           </div>
//           <div
//             className={`tab ${childTabType === '2' ? 'active' : ''}`}
//             onClick={() => {
//               setChildTabType('2');
//               setSelectedIds([]);
//             }}
//           >
//             Completed
//           </div>
//         </div>

//         {/* Table Controls */}
//         <div className="d-flex justify-content-between align-items-center cpt-15 cpb-15">
//           {selectedIds.length === 0 ? (
//             <div className="table-title">{eventName}</div>
//           ) : (
//             <div className="d-flex gap-2 h-35">
//               <div className="d-flex align-items-center gap-2 p-2 text-14-400-18 br-6 bg-f4fc">
//                 <CheckBox
//                   type="ACTIVE"
//                   isChecked={isCheckAll}
//                   onClick={handleSelectAllChange}
//                 />
//                 <span>Select All Users</span>
//               </div>
//               {createAccess === '1' && (
//                 <>
//                   <div className="d-flex align-items-center gap-2 p-2 text-14-400-18 br-6 bg-f4fc">
//                     <Button
//                       text={
//                         ['0', '1'].includes(userType)
//                           ? 'Approve'
//                           : 'Send Request'
//                       }
//                       className="h-35 border-0"
//                       onClick={() => setOpenActionForm(true)}
//                     />
//                   </div>
//                   {['0', '1'].includes(userType) && (
//                     <div className="d-flex align-items-center gap-2 p-2 text-14-400-18 br-6 bg-f4fc">
//                       <Button
//                         text="Reject"
//                         className="h-35 border-0"
//                         onClick={() => setOpenActionForm(true)}
//                       />
//                     </div>
//                   )}
//                 </>
//               )}
//             </div>
//           )}
//           <div style={{ width: '150px' }} className="me-3">
//             <SeachInput
//               placeholder="Search"
//               value={searchText}
//               onChange={handleSearch}
//               className="small-text-input"
//               iconClassName="small-search-icon"
//             />
//           </div>
//         </div>

//         {/* Table */}
//         <Table
//           isLoading={isLoading}
//           header={header}
//           rowData={rowData}
//           filterData={filterData}
//           searchPayload={searchPayload}
//           searchInputChange={handelChangeSearch}
//           changeOffset={handelChangePagination}
//           maxHeight="60vh"
//           showRowsLimit={['0', '1'].includes(userType)}
//         />
//       </Card>
//     </div>
//   );
// };

// export default ParticipantsRequest;
